// Abschnitt „Erkennung verbessern“ in einfachem JavaScript (Probe, siehe `kit.js`). Erzeugt dasselbe Markup wie `OcrTests.tsx`; ein Test vergleicht beide.
// Anders als die übrigen Ansichten hat dieser Abschnitt einen eigenen Zustand (Testfälle laden, Foto, Eingaben): `createOcrTests` hält ihn und zeichnet bei jeder Änderung neu.
// Alles, was von der App kommt (Schnittstelle, Foto verkleinern, Adresse der Website), wird als `deps` übergeben, damit dieser Teil weder React noch die App-Module kennt.
import { h, mount, svg } from './kit.js';

const nf = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });
const fmt = value => (value === undefined ? '–' : nf.format(value));

/** Anfangszustand des Abschnitts. */
export function initialOcrTestsState(consentUrl) {
  return {
    info: undefined, error: '', busy: false, message: '', consent: false,
    listName: '', libraryName: '', siteText: '', defaultSite: '',
    photoName: '', expected: '', label: '', photoUrls: {}, consentUrl
  };
}

function valueInvalid(s) {
  return s.expected.trim() !== '' && !/^\d{1,9}(,\d{1,3})?$/.test(s.expected.trim().replace(/\s/g, ''));
}

function setupForm(s, siteToInput) {
  return [
    h('p', { class: 'wb-help hint' }, 'Noch nicht eingerichtet. Die App legt eine Liste und eine Dokumentbibliothek für die Testfälle an. Auf der Website der Ablesungen müssen die Namen sich von der Ableseliste und der Fotobibliothek unterscheiden.'),
    h('div', { class: 'wb-field' },
      h('label', { for: 'test-site' }, 'SharePoint-Website (Name hinter /sites/)'),
      h('input', { id: 'test-site', type: 'text', value: s.siteText, disabled: s.busy, placeholder: 'z. B. IT-Intern', 'data-input': 'site-text' }),
      h('small', { class: 'wb-help hint' }, 'Vorbelegt mit der Website der Ablesungen. Eine andere Website des eigenen Mandanten ist möglich; die App braucht dort Zugriff, den ein Administrator erteilt, und du brauchst Besitzerrechte zum Anlegen.')),
    h('div', { class: 'wb-grid' },
      h('div', { class: 'wb-field' }, h('label', { for: 'test-list' }, 'Name der Liste (Testfälle)'), h('input', { id: 'test-list', type: 'text', value: s.listName, disabled: s.busy, 'data-input': 'list-name' })),
      h('div', { class: 'wb-field' }, h('label', { for: 'test-library' }, 'Name der Dokumentbibliothek (Testfotos)'), h('input', { id: 'test-library', type: 'text', value: s.libraryName, disabled: s.busy, 'data-input': 'library-name' }))),
    h('div', { class: 'wb-actions' }, h('button', { class: 'wb-primary', 'data-click': 'setup', disabled: s.busy || !s.listName.trim() || !s.libraryName.trim() }, 'Liste und Bibliothek anlegen'))
  ];
}

function testItem(s, item) {
  const badge = item.match ? 'success' : item.hasText ? 'warning' : 'error';
  return h('details', { class: 'wb-disclosure', 'data-toggle': 'show-photo', 'data-id': item.id },
    h('summary', {}, item.label || 'Testfall',
      h('span', { class: 'wb-disclosure-meta' }, h('span', { class: `wb-badge ${badge}` }, item.match ? 'Treffer' : item.hasText ? 'Abweichung' : 'kein Text'))),
    h('div', { class: 'wb-disclosure-content' },
      h('dl', { class: 'wb-info-grid' },
        h('dt', {}, 'Richtig'), h('dd', { class: 'short' }, fmt(item.expected)),
        h('dt', {}, 'Erkannt (jetzt)'), h('dd', { class: 'short' }, fmt(item.recognized)),
        item.recognizedAtCapture !== item.recognized && [h('dt', {}, 'Erkannt (bei Aufnahme)'), h('dd', { class: 'short' }, fmt(item.recognizedAtCapture))],
        item.confidence !== undefined && [h('dt', {}, 'Sicherheit'), h('dd', { class: 'short' }, `${Math.round(item.confidence * 100)} %`)],
        item.rawText && [h('dt', {}, 'Rohtext'), h('dd', {}, h('pre', { class: 'raw-text' }, item.rawText))]),
      s.photoUrls[item.id] && h('img', { class: 'test-photo', src: s.photoUrls[item.id], alt: `Testfoto ${item.label}` }),
      h('div', { class: 'wb-actions' },
        h('button', { class: 'wb-danger-outline wb-icon-button', title: 'Testfall löschen', 'aria-label': `Testfall ${item.label} löschen`, 'data-click': 'remove', 'data-id': item.id, disabled: s.busy },
          svg('svg', { viewBox: '0 0 24 24', 'aria-hidden': 'true' }, svg('path', { d: 'M4 7h16M10 7V4h4v3M6 7l1 13h10l1-13M10 11v6M14 11v6' }))))));
}

function configured(s, siteToInput) {
  const info = s.info;
  const invalid = valueInvalid(s);
  const summary = info.summary.total === 0
    ? 'Noch keine Testfälle.'
    : `${info.summary.total} Testfälle · Treffer bei ${info.summary.matched} (${Math.round(100 * info.summary.matched / info.summary.total)} %) · Abweichungen ${info.summary.differing} · ohne erkannten Text ${info.summary.noText}`;
  return [
    h('p', { class: 'wb-help hint' }, `Liste „${info.list}“ und Bibliothek „${info.library}“ auf der Website ${siteToInput(info.siteUrl) || info.siteUrl}.`),
    h('p', { class: 'wb-notice info', role: 'status' }, summary),
    h('div', { class: 'wb-grid' },
      h('div', { class: 'wb-field' },
        h('label', { for: 'test-label' }, 'Bezeichnung (z. B. Zählertyp und Register)'),
        h('input', { id: 'test-label', type: 'text', value: s.label, disabled: s.busy, placeholder: 'z. B. Iskra MT631, 2.8.0', 'data-input': 'label' })),
      h('div', { class: 'wb-field' },
        h('label', { for: 'test-expected' }, 'Richtiger Zählerstand'),
        h('input', { id: 'test-expected', type: 'text', inputmode: 'decimal', value: s.expected, disabled: s.busy, 'aria-invalid': String(invalid), 'data-input': 'expected' }),
        h('small', { class: 'wb-help hint' }, invalid ? 'Bitte nur Ziffern eingeben, Dezimalstellen mit Komma.' : 'Ohne Tausenderpunkt eingeben, Dezimalstellen mit Komma.')),
      h('div', { class: 'wb-field' },
        h('label', { for: 'test-photo-input' }, 'Zählerfoto'),
        h('input', { hidden: true, id: 'test-photo-input', type: 'file', accept: 'image/*,.heic,.heif', disabled: s.busy, 'data-change': 'photo' }),
        h('label', { for: 'test-photo-input', class: 'wb-file-button' }, s.photoName ? 'Foto ändern' : 'Foto wählen'),
        s.photoName && h('small', { class: 'wb-help hint' }, s.photoName))),
    h('div', { class: 'wb-actions' },
      h('button', { class: 'wb-primary', 'data-click': 'add', disabled: s.busy || !s.photoName || !s.expected.trim() || invalid || !info.ocrConfigured }, 'Prüfen und speichern'),
      h('button', { class: 'wb-secondary', 'data-click': 'export', disabled: s.busy || info.items.length === 0 }, 'Testfälle exportieren')),
    s.message && h('p', { class: 'wb-help hint', role: 'status' }, s.message),
    info.items.map(item => testItem(s, item))
  ];
}

/** Ansicht des Abschnitts aus dem Zustand (`siteToInput`: volle Adresse → Anzeige im Feld). */
export function ocrTestsView(s, siteToInput) {
  const info = s.info;
  return h('section', { class: 'wb-form-section spaced' },
    h('h2', {}, 'Erkennung verbessern'),
    h('p', { class: 'wb-help hint' }, 'Zählerfotos mit dem richtigen Zählerstand sammeln und prüfen, ob die Erkennung ihn trifft. Die Testfälle liegen getrennt von den Ablesungen in einer eigenen Liste mit eigener Bibliothek.'),
    s.error && !s.consent && h('div', { class: 'wb-notice error', role: 'alert' }, h('p', {}, s.error)),
    s.consent && h('div', { class: 'wb-notice warning', role: 'status' },
      h('p', {}, 'Die App darf noch nicht in deinem Namen Listen anlegen. Dafür muss ein Administrator einmalig zustimmen (delegierte Berechtigung „Sites.Manage.All“). Mit der Schaltfläche öffnet sich die Microsoft-Seite: Als Administrator stimmst du dort direkt zu, andernfalls kannst du die Genehmigung dort bei den Administratoren anfordern. Danach hier erneut „Liste und Bibliothek anlegen“ wählen.'),
      h('div', { class: 'wb-actions' },
        h('button', { class: 'wb-secondary', 'data-click': 'consent' }, 'Zustimmung des Administrators anfordern'),
        h('button', { class: 'wb-secondary', 'data-click': 'setup', disabled: s.busy }, 'Erneut versuchen'))),
    info && !info.ocrConfigured && h('div', { class: 'wb-notice warning' }, 'Der OCR-Dienst (Azure AI Vision) ist in der Function App noch nicht eingerichtet (Einstellung VISION_KEY fehlt). Neue Testfälle lassen sich erst danach anlegen.'),
    info && !info.configured && setupForm(s, siteToInput),
    info && info.configured && configured(s, siteToInput));
}

function errorText(error) { return error instanceof Error ? error.message : String(error); }

/**
 * Hängt den Abschnitt in `root` ein und hält seinen Zustand.
 * deps: api { fetchOcrTests, createOcrTest, deleteOcrTest, fetchOcrTestPhoto, setupOcrTests }, isConsentRequired(fehler), normalizePhoto(datei), siteFromInput(text, herkunft), siteToInput(adresse),
 * consentUrl, openLink(adresse), confirm(text), download(dateiname, text).
 */
export function createOcrTests(root, deps) {
  const state = initialOcrTestsState(deps.consentUrl);
  let photo;
  const urls = {};
  let alive = true;
  const origin = url => { try { return new URL(url).origin; } catch { return ''; } };
  const view = s => ocrTestsView(s, deps.siteToInput);
  const mounted = mount(root, view, {});
  const draw = () => { if (alive) mounted.update(state, actions); };

  async function load() {
    state.busy = true; state.error = ''; draw();
    try {
      const result = await deps.api.fetchOcrTests();
      state.info = result;
      if (!result.configured) {
        state.listName = state.listName || result.defaults.list; state.libraryName = state.libraryName || result.defaults.library;
        state.defaultSite = result.defaults.siteUrl; state.siteText = state.siteText || deps.siteToInput(result.defaults.siteUrl);
      }
    } catch (caught) { state.error = errorText(caught); }
    finally { state.busy = false; draw(); }
  }


  async function setup() {
    state.busy = true; state.error = ''; state.consent = false; state.message = ''; draw();
    try {
      await deps.api.setupOcrTests(state.listName.trim(), state.libraryName.trim(), deps.siteFromInput(state.siteText, origin(state.defaultSite)));
      await load();
      state.message = 'Liste und Bibliothek sind angelegt.';
    } catch (caught) {
      if (deps.isConsentRequired(caught)) state.consent = true;
      state.error = errorText(caught);
    } finally { state.busy = false; draw(); }
  }

  async function add() {
    if (!photo) return;
    state.busy = true; state.error = ''; state.message = ''; draw();
    try {
      const created = await deps.api.createOcrTest(await deps.normalizePhoto(photo), state.expected.trim(), state.label.trim());
      state.message = created.match
        ? `Treffer: erkannt ${fmt(created.recognized)}, richtig ${fmt(created.expected)}.`
        : created.hasText ? `Abweichung: erkannt ${fmt(created.recognized)}, richtig ${fmt(created.expected)}. Der Testfall ist gespeichert.` : 'Auf dem Foto wurde kein Text erkannt. Der Testfall ist gespeichert.';
      photo = undefined; state.photoName = ''; state.expected = ''; state.label = '';
      await load();
    } catch (caught) { state.error = errorText(caught); }
    finally { state.busy = false; draw(); }
  }

  async function remove(id) {
    const item = state.info && state.info.items.find(entry => entry.id === id);
    if (!item || !deps.confirm(`Testfall „${item.label}“ mit Foto löschen?`)) return;
    state.busy = true; state.error = ''; draw();
    try { await deps.api.deleteOcrTest(id); await load(); }
    catch (caught) { state.error = errorText(caught); state.busy = false; draw(); }
  }

  async function showPhoto(id) {
    if (urls[id]) return;
    try {
      urls[id] = await deps.api.fetchOcrTestPhoto(id);
      state.photoUrls = { ...urls };
    } catch (caught) { state.error = errorText(caught); }
    draw();
  }

  function exportTests() {
    if (!state.info || !state.info.configured) return;
    const data = state.info.items.map(item => ({ bezeichnung: item.label, sollwert: item.expected, erkannt: item.recognized, treffer: item.match, rohtext: item.rawText, angelegtAm: item.createdAt }));
    deps.download('PV-OCR-Testfaelle.json', JSON.stringify(data, null, 2));
  }

  const actions = {
    'site-text': el => { state.siteText = el.value; draw(); },
    'list-name': el => { state.listName = el.value; draw(); },
    'library-name': el => { state.libraryName = el.value; draw(); },
    label: el => { state.label = el.value; draw(); },
    expected: el => { state.expected = el.value; draw(); },
    photo: el => { photo = el.files && el.files[0]; state.photoName = photo ? photo.name : ''; draw(); },
    setup: () => void setup(),
    add: () => void add(),
    export: () => exportTests(),
    consent: () => void deps.openLink(state.consentUrl),
    remove: el => void remove(el.getAttribute('data-id')),
    // „photo“ ist das Auswählen der Datei (change); „show-photo“ das Aufklappen eines Testfalls (toggle)
    'show-photo': el => { if (el.open) void showPhoto(el.getAttribute('data-id')); }
  };

  draw();
  void load();
  return {
    destroy() {
      alive = false;
      mounted.destroy();
      Object.values(urls).forEach(url => URL.revokeObjectURL(url));
    }
  };
}
